/**
 * source-mode-card.tsx — Nguồn AI và lượt AI › "chọn nguồn AI rồi chọn model của tổ chức" (góp ý chủ sản phẩm 08/10):
 *
 *   Bước 1 "Bạn dùng nguồn AI nào?" — 2 thẻ chọn một, MỘT nguồn cho cả tổ chức: AI của eCDP (không cần key, trừ credit; số
 *     dư ví + Mua thêm) / Key của tôi (API key riêng, không trừ credit). Bấm thẻ = XEM nguồn đó ở bước 2 (chưa đổi gì).
 *     Đổi nguồn xảy ra khi bấm "Dùng model này" ở bước 2 (`PUT /ai/sources { defaultSource, providers: {<mọi nền tảng>:
 *     nguồn} }` rồi `PUT /ai/config`). Tổ chức đang dùng KẾT HỢP (dữ liệu cũ) → dòng "Đang dùng kết hợp …" + nút thống
 *     nhất về một nguồn. Không còn chọn nguồn riêng từng nền tảng.
 *   Bước 2 "Chọn model của tổ chức" — lưới thẻ model + khung cài đặt model (source-models-panels.tsx).
 * Logic thuần: source-mode-lib.ts.
 */
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, Check, Coins, KeyRound, Loader2, Plus } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { AiConfig } from '@/hooks/use-ai'
import { useAiCredits, useAiModels, useAiSources, useUpdateAiSources, useCanBuyAiCredits } from '@/hooks/use-ai-credits'
import { aiErrorMessage, findModel, SOURCE_LABEL, walletHeadline, type AiSource } from '@/lib/ai-credits'
import { supportedProvidersOf } from '@/lib/ai-providers'
import { cn } from '@/lib/utils'
import { OrgModelStep } from './source-models-panels'
import { mixedSummary, orgSourceMode, unifySourcesBody, viewedSource } from './source-mode-lib'

export function SourceModeSection({ config, canEdit, onBuy }: { config: AiConfig; canEdit: boolean; onBuy: () => void }) {
  const { t } = useTranslation('aiOps')
  const { data: sources, isLoading } = useAiSources()
  const { data: credits } = useAiCredits()
  // "Mua thêm" theo quyền mua lượt AI (ai_credits.create), không theo quyền sửa AI.
  const canBuy = useCanBuyAiCredits()
  const { data: modelsData } = useAiModels()
  const update = useUpdateAiSources()
  const [picked, setPicked] = useState<AiSource | null>(null)

  const providers = sources?.providers ?? []
  const mode = orgSourceMode(providers)
  const view = viewedSource(picked, mode)
  // Tên nền tảng: ưu tiên GET /ai/sources, dự phòng danh sách nền tảng của /ai/config.
  const names = providers.length
    ? providers.map((p) => ({ provider: p.provider, name: p.name }))
    : supportedProvidersOf(config.availableProviders).map((p) => ({ provider: p.id, name: p.name }))
  const keyed = providers.filter((p) => p.byok.configured).length
  const missingKeys = providers.filter((p) => !p.byok.configured).map((p) => p.name)
  const orgModel = findModel(modelsData?.models ?? [], config.provider, config.model)
  const orgModelName = orgModel?.title ?? config.model

  function unify(source: AiSource) {
    if (!providers.length) return
    if (source === 'byok' && missingKeys.length > 0
      && !window.confirm(t('sourceModeCard.confirmMissingKeys', { providers: missingKeys.join(', ') }))) return
    update.mutate(unifySourcesBody(providers, source), {
      onSuccess: () => { setPicked(source); toast.success(t('sourceModeCard.unifiedToast', { source: SOURCE_LABEL[source] })) },
      onError: (err) => toast.error(aiErrorMessage(err)),
    })
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('sourceModeCard.step1Title')}</CardTitle>
          <CardDescription>
            {t('sourceModeCard.step1Desc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('sourceModeCard.loading')}</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2" role="radiogroup" aria-label={t('sourceModeCard.groupAria')}>
              <SourceTile
                selected={view === 'platform'} current={mode === 'platform'} onSelect={() => setPicked('platform')}
                icon={<Coins className="h-5 w-5" />} title={t('sourceModeCard.platform.title')}
                desc={t('sourceModeCard.platform.desc')}
                footer={(
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="text-muted-foreground">
                      {credits ? <Trans t={t} i18nKey="sourceModeCard.balanceLeft" values={{ amount: walletHeadline(credits).main }} components={{ b: <b className="tabular-nums text-foreground" /> }} /> : t('sourceModeCard.orgCredits')}
                    </span>
                    {canBuy && <Button size="sm" variant="outline" onClick={onBuy}><Plus className="h-3.5 w-3.5" /> {t('sourceModeCard.buyMore')}</Button>}
                  </div>
                )}
              />
              <SourceTile
                selected={view === 'byok'} current={mode === 'byok'} onSelect={() => setPicked('byok')}
                icon={<KeyRound className="h-5 w-5" />} title={t('sourceModeCard.byok.title')}
                desc={t('sourceModeCard.byok.desc')}
                footer={<span className="text-xs text-muted-foreground">{providers.length ? t('sourceModeCard.keyedCount', { keyed, total: providers.length }) : t('sourceModeCard.noKeys')}</span>}
              />
            </div>
          )}

          {mode === 'mixed' && (
            <div role="status" className="flex flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
              <span className="min-w-0 flex-1"><Trans t={t} i18nKey="sourceModeCard.mixedNotice" values={{ summary: mixedSummary(providers) }} components={{ b: <b /> }} /></span>
              {canEdit && (
                <span className="flex flex-wrap gap-2">
                  {(['platform', 'byok'] as const).map((s) => (
                    <Button key={s} size="sm" variant="outline" disabled={update.isPending} onClick={() => unify(s)}>{t('sourceModeCard.useSource', { source: SOURCE_LABEL[s] })}</Button>
                  ))}
                </span>
              )}
            </div>
          )}

          {view && mode !== 'mixed' && view !== mode && providers.length > 0 && (
            <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
              <Trans
                t={t} i18nKey="sourceModeCard.viewingNotice"
                values={{ view: SOURCE_LABEL[view], current: mode === 'none' ? t('sourceModeCard.noSourceChosen') : SOURCE_LABEL[mode] }}
                components={{ b: <b /> }}
              />
            </p>
          )}
        </CardContent>
      </Card>

      {view && (
        <Card>
          <CardHeader>
            <CardTitle>{t('sourceModeCard.step2Title', { source: SOURCE_LABEL[view] })}</CardTitle>
            <CardDescription>
              <Trans
                t={t} i18nKey={view === 'platform' ? 'sourceModeCard.step2DescPlatform' : 'sourceModeCard.step2DescByok'}
                values={{ model: orgModelName }} components={{ b: <b className="text-foreground" /> }}
              />
            </CardDescription>
          </CardHeader>
          <CardContent>
            <OrgModelStep
              key={view} config={config} view={view} mode={mode} sources={sources} credits={credits} names={names}
              canEdit={canEdit} onPickByok={() => setPicked('byok')}
            />
          </CardContent>
        </Card>
      )}
    </>
  )
}

function SourceTile({ selected, current, onSelect, icon, title, desc, footer }: {
  selected: boolean
  current: boolean
  onSelect: () => void
  icon: ReactNode
  title: string
  desc: string
  footer: ReactNode
}) {
  const { t } = useTranslation('aiOps')
  return (
    <div className={cn('flex flex-col rounded-xl border transition-colors', selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/40')}>
      <button type="button" role="radio" aria-checked={selected} onClick={onSelect} className="flex flex-1 items-start gap-3 p-4 text-left">
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2 text-base font-semibold">
            {title}
            {current && <Badge variant="success" className="gap-1 text-[10px]"><Check className="h-3 w-3" /> {t('shared.inUse')}</Badge>}
          </span>
          <span className="mt-1 block text-sm text-muted-foreground">{desc}</span>
        </span>
      </button>
      <div className="border-t px-4 py-2">{footer}</div>
    </div>
  )
}
